import { CurrencyPipe } from "@angular/common";
import { Component, Input, OnInit, inject } from "@angular/core";
import { MatProgressBar } from "@angular/material/progress-bar";
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource } from "@angular/material/table";
import { LayoutAlignDirective, LayoutDirective, FlexFillDirective } from "@ngbracket/ngx-layout/flex";
import { AccountViewModel, BudgetSummaryCriteria, ExecutionResult, IAccountBudgetSummary } from "@tylertech/tyler-api-types";
import { AccountLookupService } from "../../services";

type AccountColumn = "accountNumber" | "budget" | "encumbrances" | "reserves" | "priorYearAdjustments" | "totalActivity" | "budgetRemaining";
interface AccountTableRow extends AccountViewModel
{
    hideReserve?: boolean;

    budget?: number;
    encumbrances?: number;
    reserves?: number;
    priorYearAdjustment?: number;
    totalActivity?: number;
    budgetRemaining?: number;
}

@Component({
    selector: "gl-account-budget-recap-table",
    templateUrl: "./accountBudgetRecapTable.tmpl.html",
    styleUrls: [
        "./accountBudgetRecapTable.component.scss"
    ],
    imports: [
        CurrencyPipe,
        LayoutAlignDirective,
        LayoutDirective,
        FlexFillDirective,
        MatCell,
        MatCellDef,
        MatColumnDef,
        MatHeaderCell,
        MatHeaderCellDef,
        MatHeaderRow,
        MatHeaderRowDef,
        MatProgressBar,
        MatRow,
        MatRowDef,
        MatTable,
    ]
})
export class AccountBudgetRecapTableComponent implements OnInit
{
    private $accountLookupService = inject(AccountLookupService);

    readonly tableData: MatTableDataSource<AccountTableRow>;
    readonly columnNames: ReadonlyArray<AccountColumn> = [
        "accountNumber",
        "budget",
        "encumbrances",
        "reserves",
        "priorYearAdjustments",
        "totalActivity",
        "budgetRemaining"
    ];

    @Input() readonly accounts: Array<AccountViewModel>;
    @Input() readonly summaryDate: Date | string;

    isLoaded: boolean;

    constructor()
    {
        this.tableData = new MatTableDataSource<AccountTableRow>();
    }

    ngOnInit(): void
    {
        this.tableData.data = this.accounts.map(x =>
        {
            return { ...x } as AccountTableRow;
        });

        const criteria: BudgetSummaryCriteria = {
            Date: typeof this.summaryDate === "string"
                ? new Date(this.summaryDate)
                : this.summaryDate,
            AccountNumbers: this.accounts.map(x => x.Number),
        };
        this.$accountLookupService.GetBudgetSummary(criteria)
            .then((executionResult: ExecutionResult<Array<IAccountBudgetSummary>>) =>
            {
                if (!executionResult.Result)
                {
                    return;
                }

                executionResult.Result
                    .forEach((accountBudgetSummary: IAccountBudgetSummary) =>
                    {
                        const accountNumber = accountBudgetSummary.AccountNumber;
                        const summary = accountBudgetSummary.Summary;
                        const row = this.tableData.data.find(x => x.Number === accountNumber);
                        if (!row)
                        {
                            return;
                        }

                        row.budget = summary.BudgetCurrent;
                        row.encumbrances = summary.Encumbrance;
                        row.reserves = summary.Reserves;
                        row.totalActivity = summary.Activity;
                        row.budgetRemaining = summary.BudgetRemaining;
                        row.priorYearAdjustment = summary.PriorYearAdjustment;

                        if (accountBudgetSummary.HideReserve)
                        {
                            row.hideReserve = accountBudgetSummary.HideReserve;
                        }
                    });
            })
            .finally(
                () =>
                {
                    this.isLoaded = true;
                });
    }
}
